import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Baby, ThumbsUp, ThumbsDown, Crown } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function NameAdvisor({ eventId, isOwner }) {
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    gender: "neutral",
    submitted_by: ""
  });

  useEffect(() => {
    fetchNames();
  }, [eventId]);

  const fetchNames = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/name-advisor`);
      setNames(response.data);
    } catch (error) {
      console.error("Failed to fetch names:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.submitted_by) {
      toast.error("Name und dein Name sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/name-advisor`,
        formData
      );
      
      toast.success("Namensvorschlag hinzugefügt!");
      setFormData({ name: "", gender: "neutral", submitted_by: "" });
      fetchNames();
    } catch (error) {
      console.error("Failed to add name:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleVote = async (nameId, voteType) => {
    try {
      await axios.post(`${API}/events/${eventId}/name-advisor/${nameId}/vote`, {
        vote: voteType
      });
      fetchNames();
    } catch (error) {
      console.error("Failed to vote:", error);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const sortedNames = [...names].sort((a, b) => {
    const scoreA = (a.upvotes || 0) - (a.downvotes || 0);
    const scoreB = (b.upvotes || 0) - (b.downvotes || 0);
    return scoreB - scoreA;
  });

  const byGender = {
    male: sortedNames.filter(n => n.gender === 'male'),
    female: sortedNames.filter(n => n.gender === 'female'),
    neutral: sortedNames.filter(n => n.gender === 'neutral')
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Baby className="w-5 h-5 text-primary" />
          Namens-Ratgeber
        </h3>
        <p className="text-sm text-muted-foreground">
          {isOwner 
            ? "Sammle Namensvorschläge von deinen Gästen"
            : "Schlage Namen vor und stimme ab!"
          }
        </p>
      </div>

      {/* Add Name Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Namensvorschlag *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="z.B. Emma, Leon"
              required
            />
          </div>
          <div>
            <Label>Geschlecht</Label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background"
            >
              <option value="male">Junge</option>
              <option value="female">Mädchen</option>
              <option value="neutral">Neutral</option>
            </select>
          </div>
        </div>

        <div>
          <Label>Dein Name *</Label>
          <Input
            value={formData.submitted_by}
            onChange={(e) => setFormData({ ...formData, submitted_by: e.target.value })}
            placeholder="Wer schlägt den Namen vor?"
            required
          />
        </div>

        <Button type="submit" className="w-full">
          Namensvorschlag einreichen
        </Button>
      </form>

      {/* Names by Gender */}
      <div className="space-y-6">
        {/* Boys */}
        {byGender.male.length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <span className="text-blue-600">👶 Jungennamen</span>
              <span className="text-xs text-muted-foreground">({byGender.male.length})</span>
            </h4>
            <div className="space-y-2">
              {byGender.male.map((name, idx) => (
                <NameCard key={name.id} name={name} rank={idx + 1} onVote={handleVote} eventId={eventId} color="blue" />
              ))}
            </div>
          </div>
        )}

        {/* Girls */}
        {byGender.female.length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <span className="text-pink-600">👧 Mädchennamen</span>
              <span className="text-xs text-muted-foreground">({byGender.female.length})</span>
            </h4>
            <div className="space-y-2">
              {byGender.female.map((name, idx) => (
                <NameCard key={name.id} name={name} rank={idx + 1} onVote={handleVote} eventId={eventId} color="pink" />
              ))}
            </div>
          </div>
        )}

        {/* Neutral */}
        {byGender.neutral.length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <span className="text-purple-600">✨ Neutrale Namen</span>
              <span className="text-xs text-muted-foreground">({byGender.neutral.length})</span>
            </h4>
            <div className="space-y-2">
              {byGender.neutral.map((name, idx) => (
                <NameCard key={name.id} name={name} rank={idx + 1} onVote={handleVote} eventId={eventId} color="purple" />
              ))}
            </div>
          </div>
        )}

        {names.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Baby className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Namensvorschläge</p>
            <p className="text-xs mt-1">Sei der Erste!</p>
          </div>
        )}
      </div>
    </Card>
  );
}

function NameCard({ name, rank, onVote, eventId, color }) {
  const score = (name.upvotes || 0) - (name.downvotes || 0);
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    pink: 'bg-pink-50 border-pink-200',
    purple: 'bg-purple-50 border-purple-200'
  };

  return (
    <div className={`p-3 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1">
          {rank <= 3 && <Crown className={`w-4 h-4 ${rank === 1 ? 'text-yellow-500' : 'text-gray-400'}`} />}
          <div className="flex-1">
            <p className="font-medium text-lg">{name.name}</p>
            <p className="text-xs text-muted-foreground">von {name.submitted_by}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onVote(name.id, 'up')}
            className="p-2 rounded hover:bg-green-100 transition-colors"
          >
            <ThumbsUp className="w-4 h-4 text-green-600" />
          </button>
          <span className={`font-bold min-w-[2rem] text-center ${
            score > 0 ? 'text-green-600' : score < 0 ? 'text-red-600' : 'text-muted-foreground'
          }`}>
            {score > 0 ? '+' : ''}{score}
          </span>
          <button
            onClick={() => onVote(name.id, 'down')}
            className="p-2 rounded hover:bg-red-100 transition-colors"
          >
            <ThumbsDown className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
}